/**
 * Typed wrappers around the `/admin/*` endpoints.
 *
 * Pure functions returning parsed bodies. Cookie auth rides via
 * `credentials: 'include'`. TanStack Query hooks live in
 * `useAdmin.ts` so this file stays tree-shake-friendly when imported
 * from a non-admin surface.
 */
import { apiBaseUrl, AuthApiError } from '../account/authApi';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* empty / non-JSON */
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

// --- Overview (F2) ---------------------------------------------------

export interface AdminOverview {
  users_total: number;
  users_by_role: {
    standard: number;
    teacher: number;
    moderator: number;
    admin: number;
  };
  users_suspended: number;
  classrooms_total: number;
  classrooms_paused: number;
  classrooms_allow_publish: number;
  projects_total: number;
  submissions_total: number;
  submissions_by_status: {
    pending: number;
    awaiting_parent_consent: number;
    awaiting_teacher_review: number;
    awaiting_moderator: number;
    processing: number;
    approved: number;
    rejected: number;
    withdrawn: number;
  };
  submissions_awaiting_review: number;
  published_apps_total: number;
  published_apps_total_views: number;
  open_reports: number;
  open_messages: number;
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return request<AdminOverview>('/admin/overview');
}

// --- Users (F3 / F4) -------------------------------------------------

export type AdminUserRole = 'standard' | 'teacher' | 'moderator' | 'admin';

export interface AdminUserRow {
  id: string;
  email: string;
  role: AdminUserRole;
  suspended_at: string | null;
  classroom_id: string | null;
  parent_user_id: string | null;
  last_seen_at: string | null;
  created_at: string;
}

export interface AdminUserList {
  rows: AdminUserRow[];
  total: number;
}

export interface AdminUserDetail {
  id: string;
  email: string;
  role: AdminUserRole;
  suspended_at: string | null;
  suspended_reason: string | null;
  classroom_id: string | null;
  parent_user_id: string | null;
  child_display_name: string | null;
  birth_year: number | null;
  last_seen_at: string | null;
  created_at: string;
  updated_at: string;
  project_count: number;
  submission_count: number;
  published_app_count: number;
}

export interface ListUsersParams {
  q?: string;
  role?: AdminUserRole;
  suspended?: boolean;
  limit?: number;
  offset?: number;
}

function buildQuery(params: ListUsersParams): string {
  const u = new URLSearchParams();
  if (params.q) u.set('q', params.q);
  if (params.role) u.set('role', params.role);
  if (typeof params.suspended === 'boolean') u.set('suspended', String(params.suspended));
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export async function listAdminUsers(params: ListUsersParams = {}): Promise<AdminUserList> {
  return request<AdminUserList>(`/admin/users${buildQuery(params)}`);
}

export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`);
}

export async function changeAdminUserRole(
  id: string,
  role: AdminUserRole,
): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function suspendAdminUser(id: string, reason: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function unsuspendAdminUser(id: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}/unsuspend`, {
    method: 'POST',
  });
}

export async function deleteAdminUser(id: string): Promise<void> {
  await request<void>(`/admin/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// --- Classroom oversight (F6) --------------------------------------

export interface AdminClassroomRow {
  id: string;
  name: string;
  class_code: string;
  teacher_user_id: string;
  teacher_email: string;
  paused_at: string | null;
  allow_publish: boolean;
  student_count: number;
  created_at: string;
}

export interface AdminClassroomList {
  rows: AdminClassroomRow[];
  total: number;
}

export interface ListClassroomsParams {
  q?: string;
  paused?: boolean;
  limit?: number;
  offset?: number;
}

export async function listAdminClassrooms(
  params: ListClassroomsParams = {},
): Promise<AdminClassroomList> {
  const u = new URLSearchParams();
  if (params.q) u.set('q', params.q);
  if (typeof params.paused === 'boolean') u.set('paused', String(params.paused));
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminClassroomList>(`/admin/classrooms${qs ? `?${qs}` : ''}`);
}

export async function pauseAdminClassroom(id: string): Promise<AdminClassroomRow> {
  return request<AdminClassroomRow>(`/admin/classrooms/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
  });
}

export async function unpauseAdminClassroom(id: string): Promise<AdminClassroomRow> {
  return request<AdminClassroomRow>(`/admin/classrooms/${encodeURIComponent(id)}/unpause`, {
    method: 'POST',
  });
}

export async function setAdminClassroomAllowPublish(
  id: string,
  allow: boolean,
): Promise<AdminClassroomRow> {
  return request<AdminClassroomRow>(`/admin/classrooms/${encodeURIComponent(id)}/allow_publish`, {
    method: 'POST',
    body: JSON.stringify({ allow }),
  });
}

export async function deleteAdminClassroom(id: string): Promise<void> {
  await request<void>(`/admin/classrooms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// --- Submissions oversight (F7) ------------------------------------

export type SubmissionStatus =
  | 'pending'
  | 'awaiting_parent_consent'
  | 'awaiting_teacher_review'
  | 'awaiting_moderator'
  | 'processing'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

export interface AdminSubmissionRow {
  id: string;
  status: SubmissionStatus;
  visibility: 'public' | 'class_only';
  rejection_reason: string | null;
  submitter_id: string;
  submitter_email: string;
  project_id: string;
  project_name: string | null;
  project_kind: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminSubmissionList {
  rows: AdminSubmissionRow[];
  total: number;
}

export interface ListSubmissionsParams {
  status?: SubmissionStatus;
  visibility?: 'public' | 'class_only';
  submitter_email?: string;
  limit?: number;
  offset?: number;
}

export async function listAdminSubmissions(
  params: ListSubmissionsParams = {},
): Promise<AdminSubmissionList> {
  const u = new URLSearchParams();
  if (params.status) u.set('status', params.status);
  if (params.visibility) u.set('visibility', params.visibility);
  if (params.submitter_email) u.set('submitter_email', params.submitter_email);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminSubmissionList>(`/admin/submissions${qs ? `?${qs}` : ''}`);
}

export async function forceApproveSubmission(
  id: string,
  note?: string,
): Promise<AdminSubmissionRow> {
  return request<AdminSubmissionRow>(`/admin/submissions/${encodeURIComponent(id)}/force_approve`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  });
}

export async function forceRejectSubmission(
  id: string,
  reason: string,
): Promise<AdminSubmissionRow> {
  return request<AdminSubmissionRow>(`/admin/submissions/${encodeURIComponent(id)}/force_reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function withdrawSubmission(id: string): Promise<AdminSubmissionRow> {
  return request<AdminSubmissionRow>(`/admin/submissions/${encodeURIComponent(id)}/withdraw`, {
    method: 'POST',
  });
}

// --- Published apps oversight (F10) --------------------------------

export interface AdminPublishedAppRow {
  id: string;
  slug: string;
  title: string;
  version: number;
  view_count: number;
  user_id: string;
  user_email: string;
  storage_path: string;
  share_url: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPublishedAppList {
  rows: AdminPublishedAppRow[];
  total: number;
}

export interface ListPublishedAppsParams {
  q?: string;
  user_email?: string;
  limit?: number;
  offset?: number;
}

export async function listAdminPublishedApps(
  params: ListPublishedAppsParams = {},
): Promise<AdminPublishedAppList> {
  const u = new URLSearchParams();
  if (params.q) u.set('q', params.q);
  if (params.user_email) u.set('user_email', params.user_email);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminPublishedAppList>(`/admin/published-apps${qs ? `?${qs}` : ''}`);
}

export async function takedownPublishedApp(id: string, reason: string): Promise<void> {
  await request<void>(`/admin/published-apps/${encodeURIComponent(id)}/takedown`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// --- Inbound messages (Phase 5) ------------------------------------

export type InboundKind = 'contact' | 'feature' | 'bug';
export type InboundStatus = 'open' | 'handled' | 'spam' | 'wontfix';

export interface AdminMessageRow {
  id: string;
  kind: InboundKind;
  subject: string;
  body: string;
  sender_name: string | null;
  sender_email: string | null;
  sender_user_id: string | null;
  sender_username: string | null;
  status: InboundStatus;
  handled_by_email: string | null;
  handled_at: string | null;
  handled_note: string | null;
  created_at: string;
}

export interface AdminMessagePage {
  rows: AdminMessageRow[];
  total: number;
}

export interface ListMessagesParams {
  status?: InboundStatus | 'all';
  kind?: InboundKind;
  limit?: number;
  offset?: number;
}

export async function listAdminMessages(
  params: ListMessagesParams = {},
): Promise<AdminMessagePage> {
  const u = new URLSearchParams();
  if (params.status) u.set('status', params.status);
  if (params.kind) u.set('kind', params.kind);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminMessagePage>(`/admin/messages${qs ? `?${qs}` : ''}`);
}

export async function handleAdminMessage(
  id: string,
  status: InboundStatus,
  note?: string,
): Promise<AdminMessageRow> {
  return request<AdminMessageRow>(`/admin/messages/${encodeURIComponent(id)}/handle`, {
    method: 'POST',
    body: JSON.stringify({ status, ...(note ? { note } : {}) }),
  });
}

// --- Reports queue (F8) --------------------------------------------

export interface AdminReportRow {
  id: string;
  submission_id: string;
  submission_status: string | null;
  submitter_email: string | null;
  project_name: string | null;
  reason: string;
  detail: string | null;
  reporter_id: string | null;
  disposition: 'dismissed' | 'actioned' | null;
  disposed_at: string | null;
  disposed_by_email: string | null;
  disposed_note: string | null;
  created_at: string;
}

export interface AdminReportList {
  rows: AdminReportRow[];
  total: number;
}

export interface ListReportsParams {
  open_only?: boolean;
  submission_id?: string;
  limit?: number;
  offset?: number;
}

export async function listAdminReports(params: ListReportsParams = {}): Promise<AdminReportList> {
  const u = new URLSearchParams();
  if (typeof params.open_only === 'boolean') u.set('open_only', String(params.open_only));
  if (params.submission_id) u.set('submission_id', params.submission_id);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminReportList>(`/admin/reports${qs ? `?${qs}` : ''}`);
}

export async function dismissReport(id: string, note?: string): Promise<AdminReportRow> {
  return request<AdminReportRow>(`/admin/reports/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  });
}

export async function actionReport(id: string, note?: string): Promise<AdminReportRow> {
  return request<AdminReportRow>(`/admin/reports/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  });
}

// --- Bulk user actions (F5) ----------------------------------------

export type BulkUserAction = 'suspend' | 'unsuspend' | 'delete';

export interface BulkUserResultRow {
  user_id: string;
  ok: boolean;
  error: string | null;
}

export interface BulkUserResult {
  succeeded: number;
  failed: number;
  rows: BulkUserResultRow[];
}

export async function bulkAdminUserAction(input: {
  user_ids: string[];
  action: BulkUserAction;
  reason?: string;
}): Promise<BulkUserResult> {
  return request<BulkUserResult>(`/admin/users/bulk`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// --- Admin audit log (F12) ------------------------------------------

export interface AdminAuditRow {
  id: string;
  admin_user_id: string;
  admin_email: string;
  action: string;
  target_type: string;
  target_id: string | null;
  target_label: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface AdminAuditPage {
  rows: AdminAuditRow[];
  total: number;
}

export interface AdminAuditFilters {
  admin_user_id?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  limit?: number;
  offset?: number;
}

// --- System health (F17) -------------------------------------------

export interface HealthCheck {
  name: string;
  ok: boolean;
  detail: string | null;
}

export interface HealthPayload {
  overall_ok: boolean;
  checks: HealthCheck[];
  db_latency_ms: number;
  storage_provider: string;
  moderation_provider: string;
  csam_provider: string;
  email_provider: string;
}

export async function getAdminHealth(): Promise<HealthPayload> {
  return request<HealthPayload>('/admin/health');
}

// --- Feature flags (F18) -------------------------------------------

export interface FeatureFlagRow {
  id: string;
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

export interface FeatureFlagList {
  rows: FeatureFlagRow[];
}

export async function listFeatureFlags(): Promise<FeatureFlagList> {
  return request<FeatureFlagList>('/admin/flags');
}

export async function createFeatureFlag(input: {
  key: string;
  enabled: boolean;
  description?: string;
}): Promise<FeatureFlagRow> {
  return request<FeatureFlagRow>('/admin/flags', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function toggleFeatureFlag(id: string, enabled: boolean): Promise<FeatureFlagRow> {
  return request<FeatureFlagRow>(`/admin/flags/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

// --- Global search (F19) -------------------------------------------

export interface GlobalSearchHit {
  type: 'user' | 'classroom' | 'submission' | 'published_app';
  id: string;
  label: string;
  sublabel: string | null;
}

export interface GlobalSearchPayload {
  hits: GlobalSearchHit[];
  total: number;
}

export async function adminGlobalSearch(q: string): Promise<GlobalSearchPayload> {
  return request<GlobalSearchPayload>(`/admin/search?q=${encodeURIComponent(q)}`);
}

// --- Email outbox + send digests (F15 + F16) ----------------------

export interface EmailOutboxRow {
  to: string;
  subject: string;
  body_text: string;
  sent_at: string;
  provider: string;
}

export interface EmailOutboxPayload {
  rows: EmailOutboxRow[];
  total: number;
}

export async function getEmailOutbox(limit = 50): Promise<EmailOutboxPayload> {
  return request<EmailOutboxPayload>(`/admin/email-outbox?limit=${limit}`);
}

export interface SendDigestsResult {
  sent: number;
  skipped: number;
  rows: { to: string; status: string }[];
}

export async function sendDigests(dryRun: boolean): Promise<SendDigestsResult> {
  return request<SendDigestsResult>(`/admin/send-digests?dry_run=${dryRun}`, { method: 'POST' });
}

// --- Configuration view (F14) -------------------------------------

export interface AdminConfigItem {
  key: string;
  value: string | number | boolean | null;
  sensitive: boolean;
  section: string;
  description: string | null;
}

export interface AdminConfigPayload {
  items: AdminConfigItem[];
}

export async function getAdminConfig(): Promise<AdminConfigPayload> {
  return request<AdminConfigPayload>('/admin/config');
}

// --- Moderation audit (F13) ----------------------------------------

export interface ModerationAuditRow {
  id: string;
  submission_id: string;
  blob_id: string | null;
  stage: string;
  provider: string;
  decision: string;
  reason: string;
  signals: Record<string, unknown>;
  created_at: string;
}

export interface ModerationAuditPage {
  rows: ModerationAuditRow[];
  total: number;
}

export interface ModerationAuditFilters {
  submission_id?: string;
  stage?: string;
  decision?: string;
  limit?: number;
  offset?: number;
}

export async function listModerationAudit(
  params: ModerationAuditFilters = {},
): Promise<ModerationAuditPage> {
  const u = new URLSearchParams();
  if (params.submission_id) u.set('submission_id', params.submission_id);
  if (params.stage) u.set('stage', params.stage);
  if (params.decision) u.set('decision', params.decision);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<ModerationAuditPage>(`/admin/moderation-audit${qs ? `?${qs}` : ''}`);
}

// ----- Admin audit log filters (F12) -------------------------------

export async function listAdminAuditLog(params: AdminAuditFilters = {}): Promise<AdminAuditPage> {
  const u = new URLSearchParams();
  if (params.admin_user_id) u.set('admin_user_id', params.admin_user_id);
  if (params.action) u.set('action', params.action);
  if (params.target_type) u.set('target_type', params.target_type);
  if (params.target_id) u.set('target_id', params.target_id);
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<AdminAuditPage>(`/admin/audit-log${qs ? `?${qs}` : ''}`);
}
